import { Plus, BarChart2, PieChart, LineChart, ArrowLeft } from "lucide-react";
import { Link } from "~/components/Link";
import { Button } from "~/components/ui/button";
import { Routes } from "~/routes";
import { ChartsUserEvents } from "~/modules/analytics/events/charts";
import { cn } from "~/lib/utils";

// // // //

interface Props {
    title?: string;
    description?: string;
    className?: string;
    /**
     * When set, the CTA calls this handler instead of linking to the new-chart
     * page. Used to paywall chart creation for free-plan users.
     */
    onCreateChart?: () => void;
    /**
     * Renders a "View all charts" CTA (linking back to the charts list) instead
     * of the create-chart CTA. Used for the chart not-found state.
     */
    notFound?: boolean;
}

/**
 * EmptyState
 * Empty state for the charts list — a blueprint-style canvas with floating
 * chart elements behind a centered call-to-action.
 */
export function EmptyState(props: Props) {
    const {
        title = "A blank canvas",
        description = "Transform your data into beautiful charts. Build a chart or table to start your collection.",
        className,
        onCreateChart,
        notFound = false,
    } = props;

    const ctaLabel = "New Chart";

    return (
        <div
            className={cn(
                "relative flex min-h-[600px] w-full items-center justify-center overflow-hidden rounded-lg border bg-card shadow-sm",
                className,
            )}
        >
            {/* Subtle Dotted Background Canvas */}
            <div className="absolute inset-0 z-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] opacity-40 bg-size-[32px_32px]"></div>

            {/* Floating Blueprint Elements */}
            <div className="absolute left-24 top-20 z-10 hidden -rotate-6 rounded-2xl border bg-card/80 p-4 opacity-60 shadow-sm backdrop-blur-md md:block">
                <BarChart2
                    className="mb-6 h-6 w-6 text-muted-foreground"
                    strokeWidth={1.5}
                />
                <div className="space-y-1.5">
                    <div className="h-1 w-12 rounded-full bg-muted-foreground"></div>
                    <div className="h-1 w-8 rounded-full bg-muted-foreground"></div>
                </div>
            </div>

            <div className="absolute bottom-24 right-24 z-10 hidden rotate-12 rounded-2xl border bg-card/80 p-5 opacity-60 shadow-sm backdrop-blur-md md:block">
                <PieChart
                    className="mb-6 h-8 w-8 text-muted-foreground"
                    strokeWidth={1.5}
                />
                <div className="space-y-1.5">
                    <div className="h-1 w-16 rounded-full bg-muted-foreground"></div>
                    <div className="h-1 w-10 rounded-full bg-muted-foreground"></div>
                </div>
            </div>

            <div className="absolute right-36 top-32 z-10 hidden rotate-15 rounded-xl border bg-card/80 p-3 opacity-60 shadow-sm backdrop-blur-md lg:block">
                <LineChart
                    className="h-5 w-5 text-muted-foreground"
                    strokeWidth={1.5}
                />
            </div>

            {/* Central Glow for depth */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-card blur-2xl"></div>
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-50/50 blur-[80px]"></div>

            {/* Main Content */}
            <div className="relative z-20 flex max-w-lg flex-col items-center justify-center px-6 text-center">
                <div className="group relative mb-10">
                    <div className="absolute inset-0 rounded-3xl bg-blue-100/50 opacity-100 blur-xl"></div>
                    <div className="relative flex h-24 w-24 items-center justify-center rounded-3xl border bg-card shadow-sm">
                        {notFound && (
                            <LineChart
                                className="h-10 w-10 text-muted-foreground"
                                strokeWidth={1.5}
                            />
                        )}
                        {!notFound && (
                            <Plus
                                className="h-10 w-10 text-muted-foreground"
                                strokeWidth={1}
                            />
                        )}
                    </div>
                </div>

                <h2 className="mb-4 text-3xl font-medium tracking-tight text-foreground">
                    {title}
                </h2>

                <p className="mx-auto mb-10 max-w-sm text-[15px] leading-relaxed text-muted-foreground">
                    {description}
                </p>

                {notFound ? (
                    <Button
                        asChild
                        size="lg"
                        className="gap-1.5 text-base"
                        variant="primaryLight"
                    >
                        <Link
                            href={Routes.charts}
                            analyticsLabel={
                                ChartsUserEvents.ChartNotFoundClickViewCharts
                            }
                        >
                            <ArrowLeft className="size-4" aria-hidden />
                            View all charts
                        </Link>
                    </Button>
                ) : onCreateChart ? (
                    <Button
                        size="lg"
                        className="gap-1.5 text-base"
                        variant="primaryLight"
                        onClick={onCreateChart}
                    >
                        <Plus className="size-5" />
                        {ctaLabel}
                    </Button>
                ) : (
                    <Button
                        asChild
                        size="lg"
                        className="gap-1.5 text-base"
                        variant="primaryLight"
                    >
                        <Link
                            href={Routes.chartNew}
                            analyticsLabel={
                                ChartsUserEvents.ChartListClickCreateChartEmptyState
                            }
                        >
                            <Plus className="size-5" />
                            {ctaLabel}
                        </Link>
                    </Button>
                )}
            </div>

            {/* Fade out edges slightly to frame the grid */}
            <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,white_100%)]"></div>
        </div>
    );
}
